import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Toggle, Radio, Icon, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { savePaymentMethods, deletePaymentMethod, checkIdUniquenes } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import SelectionIconButton from '../../components/SelectionIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { constantsAtom, storesDictAtom, walletsDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

const paymentIcons = ['credit-card', 'money-bill', 'qrcode', 'question', 'sync-alt', 'file-contract', 'exchange-alt', 'globe', 'bitcoin']


export default function PaymentMethodDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail, detailIndex, allData } = route.params
  const [id, setId] = useState(detail ? (detail.id || '') : ('paymentMethod-' + Date.now()))
  const storesDict = useAtomValue(storesDictAtom)
  const constants = useAtomValue(constantsAtom)
  const [name, setName] = useState(detail ? (detail.name || '') : '')
  const [icon, setIcon] = useState(detail ? (detail.icon || '') : '')
  const [iconText, setIconText] = useState(detail ? (detail.iconText || '') : '')
  const [comission, setComission] = useState(detail ? (detail.comission?.toString() || '0') : '0')
  const [isCash, setIsCash] = useState(detail ? (detail.isCash || false) : false)
  const [destinationWalletForStore, setDestinationWalletForStore] = useState(detail ? (detail.destinationWalletForStore || {}) : {})
  const [activeInStore, setActiveInStore] = useState(detail ? (detail.activeInStore || {}) : {})
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // Emit detail data for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[PAYMENT_METHOD_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        icon: detail.icon,
        iconText: detail.iconText,
        comission: detail.comission,
        isCash: detail.isCash,
        activeInStore: detail.activeInStore,
      })));
    }
  }, [detail]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            void deletePaymentMethod(context.db, detailIndex, allData)
            navigation.goBack()
          }}
          iconName='trash-outline'
        />
      ),
      title: (detail?.name || '')
    })
  }, [])

  async function save() {
    setIsLoading(true)
    let uniqueId = true
    if (detailIndex == allData.length) {
      uniqueId = checkIdUniquenes(id, constants)
    }
    if (!uniqueId) {
      setSaveError(t('settings.uniqueIdError'))
      setIsLoading(false)
      return
    }
    if (name === '' && id === '' && icon === '') {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
      return
    }
    let destinationWalletsFilled = true
    Object.entries(storesDict).forEach(([key, value]) => {
      if (activeInStore[key] && !destinationWalletForStore[key]) {
        destinationWalletsFilled = false
      }
    })
    if (!destinationWalletsFilled) {
      setSaveError(t('settings.destinationWalletsNotFilledError'))
      setIsLoading(false)
      return
    }
    setSaveError('')
    let comissionFloat = parseFloat(comission)
    let info = {
      name,
      id,
      icon,
      iconText,
      comission: comissionFloat,
      isCash,
      destinationWalletForStore,
      activeInStore,
    }
    try {
      await savePaymentMethods(context.db, info, detailIndex, allData)
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
    
        <Text category='label' style={styles.label}>
          {t('settings.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />

        <Text category='label' style={styles.label}>
          {t('settings.iconAndText')}
        </Text>
        <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
          { paymentIcons.map((item, index) => (
            <SelectionIconButton 
              key={item+index}
              onPress={() => {setIcon(item)}} 
              text={iconText}
              iconName={item}
              selected={item === icon}
              style={{marginRight: 5}}
            />
          )) }
        </View>
        <StyledTextInput 
          value={iconText}
          style={{marginTop: 10}}
          onChangeText={text => setIconText(text)}
          returnKeyType='done'
        />

        <Text category='label' style={styles.label}>
          {t('settings.comission')}
        </Text>
        <StyledTextInput 
          value={comission}
          onChangeText={text => setComission(text)}
          returnKeyType='done'
          keyboardType='decimal-pad'
        />  

        <Text category='label' style={styles.label}>{t('settings.category')}</Text>
        <Radio testID="radio-cash" style={styles.radio} checked={isCash} onChange={() => {setIsCash(true)}}>
          {t('settings.cash')}
        </Radio>
        <Radio testID="radio-non-cash" style={styles.radio} checked={!isCash} onChange={() => {setIsCash(false)}}>
          {t('settings.card')}
        </Radio>

        <Text category='label' style={styles.label}>
          {t('settings.settingsByStore')} 
        </Text>
        { Object.entries(storesDict).map(([key, value]) => (
          <View key={key} style={{marginBottom: 10}}>
            <Toggle 
              checked={activeInStore[key]}
              onChange={(value) => {
                var newActive = {...activeInStore}
                newActive[key] = value
                setActiveInStore(newActive)
              }}
              style={styles.toggleField}
            >
              {storesDict?.[key].name}
            </Toggle>
            <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 5}}>
              <Text category='label'>
                { t('settings.destinationWallet') + storesDict?.[key].name }
              </Text>
              { (context.user.role === 'admin') && (
                <TouchableOpacity onPress={() => navigation.navigate('UniversalList', {atom: walletsDictAtom, searchField: 'text', detailScreen: 'WalletDetail', title: t('settings.wallets')})} style={{marginLeft: 5, padding: 3}}>
                  <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
                </TouchableOpacity>
              )}
            </View>
            <PlatformDependantSelect
              data={constants?.wallets.data}
              selectedOption={destinationWalletForStore[key]}
              onSelect={(itemValue, itemIndex) => {
                var newD = {...destinationWalletForStore}
                newD[key] = itemValue
                setDestinationWalletForStore(newD)
              }}
              style={{marginBottom: 10}}
            />
          </View>
          ))
        }
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    marginBottom: 5,
    alignSelf: 'flex-start'
  },
  radio: {
    marginBottom: 5
  },
});
